import cn from 'classnames'
import {useEffect, useRef, useState} from 'react'
import type {IconType} from 'src/common/Icon'
import {Icon} from 'src/common/Icon'

export type TabSwitcherOption<T extends string> = {
  value: T
  label: string
  icon: IconType
  iconClassName?: string
}

export const TabSwitcher = <T extends string>({
  options,
  activeValue,
  onChange,
  className,
}: {
  options: TabSwitcherOption<T>[]
  activeValue: T
  onChange: (value: T) => void
  className?: string
}) => {
  const activeIndex = options.findIndex((option) => option.value === activeValue)
  const [hasEnteredView, setHasEnteredView] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  // Nudge first-time visitors that this is interactive by briefly pulsing once it's actually visible
  useEffect(() => {
    const el = containerRef.current

    if (!el || typeof IntersectionObserver === 'undefined') {
      return undefined
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHasEnteredView(true)
          observer.disconnect()
        }
      },
      {threshold: 0.6},
    )

    observer.observe(el)

    return () => observer.disconnect()
  }, [])

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative isolate flex w-full items-center rounded-full border border-green-light p-[3px]',
        hasEnteredView && 'motion-safe:animate-tab-switcher-glow',
        className,
      )}
    >
      <div
        className="absolute left-[3px] top-[3px] h-[calc(100%-6px)] rounded-full bg-[#ffffff1f] transition-transform duration-500 ease-in-out"
        style={{
          width: `calc((100% - 6px) / ${options.length})`,
          transform: `translateX(${activeIndex * 100}%)`,
        }}
      />
      {options.map((option) => {
        const isActive = option.value === activeValue

        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            aria-pressed={isActive}
            className={cn(
              'relative z-10 flex min-w-0 flex-1 items-center justify-center gap-1.5 rounded-full p-2 text-[11px] font-semibold leading-normal tracking-[.66px] outline-none transition-colors duration-300 focus-visible:ring-2 focus-visible:ring-green-neon focus-visible:ring-offset-2 focus-visible:ring-offset-green-dark',
              isActive ? 'text-moss-white' : 'text-grey-200/70 hover:text-moss-white',
            )}
          >
            <Icon name={option.icon} className={option.iconClassName} />
            {option.label}
          </button>
        )
      })}
    </div>
  )
}
